// client/src/components/StatsView.tsx
//
// Rewrite con soporte para:
// - Fase 1: CBet Flop breakdown por matchup posicional (expandible)
// - Fase 2: Fold to 3Bet split por all-in vs no all-in
// - Fase 3: Open Raise (RFI) por posición (expandible)
// - Fase 4: 3Bet y Fold to 3Bet por matchup posicional (expandibles)
//
// Las constantes RANGES_BY_STACK y la función evalStat fueron movidas a
// ./stats/leakRanges.ts. El componente StatRow a ./stats/StatRow.tsx.

import React, { useState, useMemo } from 'react';
import { useT } from '../lib/i18n';
import {
  ParsedTournament,
  derivedStats,
  HistoryStats,
} from '../utils/pokerCraftParser';
import { emptyStats, addStats } from '../utils/pokerStats/emptyStats';
import { useParsedTournaments } from '../lib/useParsedTournaments';
import { RootState } from '../store';
import { useSelector } from 'react-redux';

import { StatRow } from './stats/StatRow';
import { UpgradePrompt } from './stats/UpgradePrompt';
import { MatchupBreakdown } from './stats/MatchupBreakdown';
import { RfiBreakdown } from './stats/RfiBreakdown';
import { VpipBreakdown } from './stats/VpipBreakdown';
import { FoldToPushBreakdown } from './stats/FoldToPushBreakdown';
import { PushFoldBreakdown } from './stats/PushFoldBreakdown';
import { CbetTextureBreakdown } from './stats/CbetTextureBreakdown';
import { CbetTextureMultiBreakdown } from './stats/CbetTextureMultiBreakdown';
import { Cbet3BetPotBreakdown } from './stats/Cbet3BetPotBreakdown';
import { RANGES_BY_STACK, StackKey } from './stats/leakRanges';
import { getCbetMatchupBenchmark } from './stats/cbetMatchupBenchmarks';
import { getThreebetMatchupBenchmark } from './stats/threebetMatchupBenchmarks';
import { getFoldTo3betMatchupBenchmark } from './stats/foldTo3betMatchupBenchmarks';
import { evalBenchmark, MIN_SAMPLE_FOR_BENCHMARK_EVAL } from './stats/benchmarks';
import { useBenchmarks } from '../contexts/BenchmarkContext';
import { supabase } from '../lib/supabase';

// ─── Filtro por fecha ──────────────────────────────────────────────────────
type DatePreset = 'all' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'custom';

function computePresetRange(preset: Exclude<DatePreset, 'custom'>): { from: string; to: string } {
  if (preset === 'all') return { from: '', to: '' };
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  if (preset === 'last7') {
    const d = new Date(now);
    d.setDate(d.getDate() - 6);
    return { from: d.toISOString().split('T')[0], to: todayStr };
  }
  if (preset === 'last30') {
    const d = new Date(now);
    d.setDate(d.getDate() - 29);
    return { from: d.toISOString().split('T')[0], to: todayStr };
  }
  if (preset === 'thisMonth') {
    const m = String(now.getMonth() + 1).padStart(2, '0');
    return { from: `${now.getFullYear()}-${m}-01`, to: todayStr };
  }
  // lastMonth
  const month = now.getMonth() === 0 ? 12 : now.getMonth();
  const year  = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const m = String(month).padStart(2, '0');
  const lastDay = new Date(year, month, 0).getDate();
  return { from: `${year}-${m}-01`, to: `${year}-${m}-${String(lastDay).padStart(2, '0')}` };
}

interface Props {
  tournaments?: ParsedTournament[];
  plan?: 'free' | 'pro';
  onUpgrade?: () => void;
}

export function StatsView({ tournaments: tournamentsProp, plan = 'free', onUpgrade }: Props) {
  const { tournaments: tournamentsCtx } = useParsedTournaments();
  const { t, lang } = useT();
  const { sessions } = useSelector((s: RootState) => s);
  const bm = useBenchmarks();

  const tournaments = useMemo(() => {
    const contextTournaments = tournamentsCtx || [];
    const savedTournaments = (sessions.list || []).flatMap(
      (s: any) => s.tournaments || []
    ) as any[];

    const unifiedMap = new Map<string, ParsedTournament>();

    savedTournaments.forEach((trn: any) => {
      const id = trn.id || trn.tournamentId;
      if (id) unifiedMap.set(id, trn as ParsedTournament);
    });

    contextTournaments.forEach((trn: ParsedTournament) => {
      const id = trn.tournamentId || (trn as any).id;
      if (id) unifiedMap.set(id, trn);
    });

    return Array.from(unifiedMap.values());
  }, [tournamentsCtx, sessions.list]);

  const isPro = plan === 'pro';

  const [aiAnalysis, setAiAnalysis] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [consent, setConsent] = useState(false);
  const [section, setSection] = useState<'preflop' | 'flop' | 'turn' | 'river' | 'showdown'>(
    'preflop'
  );
  const [activeStack, setActiveStack] = useState<StackKey>('all');

  // Fase 1: estado para expandir el matchup breakdown
  const [expandedCbetMatchups, setExpandedCbetMatchups] = useState(false);
  // Fase 2: estado para expandir el fold-to-3bet split
  const [expandedFoldTo3bet, setExpandedFoldTo3bet] = useState(false);
  // Fase 3: estado para expandir el RFI breakdown
  const [expandedRfi, setExpandedRfi] = useState(false);
  // Fase 4: estado para expandir el 3bet matchup breakdown
  const [expanded3betMatchups, setExpanded3betMatchups] = useState(false);
  // Fase 4: estado para mostrar el matchup breakdown DENTRO del split fold-to-3bet
  const [expandedFoldTo3betMatchups, setExpandedFoldTo3betMatchups] = useState(false);
  // Fase A: estado para expandir el push/fold breakdown
  const [expandedPushFold, setExpandedPushFold] = useState(false);
  // Fase B: estado para expandir el cbet por textura
  const [expandedCbetTexture, setExpandedCbetTexture] = useState(false);
  // Fase B.2: estado para expandir el cbet por textura multiway
  const [expandedCbetTextureMulti, setExpandedCbetTextureMulti] = useState(false);
  // CBet en pozo 3beteado
  const [expandedCbet3BetPot, setExpandedCbet3BetPot] = useState(false);
  // VPIP por posición: estado para expandir breakdown
  const [expandedVpipByPos, setExpandedVpipByPos] = useState(false);
  // Fase C: Fold to Push
  const [expandedFoldToPush, setExpandedFoldToPush] = useState(false);

  // Filtro por fecha
  const [activePreset, setActivePreset] = useState<DatePreset>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ─── Torneos filtrados por rango de fecha ────────────────────────────────
  const filteredTournaments = useMemo(() => {
    if (!dateFrom && !dateTo) return tournaments;
    return tournaments.filter(t => {
      const d = t.date || '';
      if (dateFrom && d && d < dateFrom) return false;
      if (dateTo   && d && d > dateTo)   return false;
      return true;
    });
  }, [tournaments, dateFrom, dateTo]);

  // ─── Aggregate stats across all tournaments (por bucket o global) ──────────
  const aggregated = useMemo<HistoryStats | null>(() => {
    if (!filteredTournaments || filteredTournaments.length === 0) return null;
    const useBucket = activeStack !== 'all';

    const agg = emptyStats();

    for (const trn of filteredTournaments) {
      const src: HistoryStats | undefined = useBucket
        ? trn.statsByStack?.[activeStack as 'short' | 'mid' | 'deep']
        : (trn as HistoryStats);
      if (!src) continue;
      addStats(agg, src);
    }

    return agg;
  }, [filteredTournaments, activeStack]);

  const stats = useMemo(() => (aggregated ? derivedStats(aggregated) : null), [aggregated]);

  // ─── Segmentation para IA (sin cambios vs versión anterior) ────────────────
  const segmentation = useMemo(() => {
    if (!filteredTournaments || filteredTournaments.length === 0) return null;
    const buckets: ('short' | 'mid' | 'deep')[] = ['short', 'mid', 'deep'];
    const res: any = {};

    for (const b of buckets) {
      const bucketAgg = emptyStats();
      for (const trn of filteredTournaments) {
        const src = trn.statsByStack?.[b];
        if (src) addStats(bucketAgg, src);
      }
      if (bucketAgg.handsPlayed === 0) {
        res[b] = { handsPlayed: 0 };
      } else {
        const ds = derivedStats(bucketAgg);
        res[b] = {
          handsPlayed: bucketAgg.handsPlayed,
          vpip: ds.vpip,
          pfr: ds.pfr,
          wsd: ds.wsd,
          wtsd: ds.wtsd,
          wwsf: ds.wwsf,
          foldTo3betAllin: ds.foldTo3betAllin,
          foldTo3betNonAllin: ds.foldTo3betNonAllin,
          cbetFlopByMatchup: ds.cbetFlopByMatchup
            .filter(m => !m.lowSample)
            .map(m => ({ key: m.key, cbetPct: m.cbetPct, opp: m.opp })),
        };
      }
    }

    const dominant = (['short', 'mid', 'deep'] as const).reduce(
      (best, cur) => ((res[cur].handsPlayed || 0) > (res[best].handsPlayed || 0) ? cur : best),
      'short' as 'short' | 'mid' | 'deep'
    );

    return { buckets: res, dominantStack: dominant };
  }, [filteredTournaments]);

  const criticalLeaks = useMemo(() => {
    if (!stats) return [];

    // ── Escalares planos (RANGES_BY_STACK) ──────────────────────────────────
    const scalarLeaks = Object.entries(RANGES_BY_STACK[activeStack] || RANGES_BY_STACK.all)
      .map(([key, r]) => {
        const value = stats[key as keyof typeof stats] as number | undefined;
        if (typeof value !== 'number') return null;
        const status = bm.evalStat(key, value, activeStack);
        if (status !== 'critical') return null;
        return { key, label: r.label, value };
      })
      .filter(Boolean) as { key: string; label: string; value: number }[];

    // ── CBet Flop por textura × IP/OOP (HU) ─────────────────────────────────
    const texLabel: Record<string, string> = {
      dry: 'Dry', 'semi-wet': 'Semi-wet', wet: 'Wet', paired: 'Paired', monotone: 'Monotone',
    };
    const textureLeaks = stats.cbetFlopByTexture
      .filter(c => c.opp >= MIN_SAMPLE_FOR_BENCHMARK_EVAL)
      .map(c => {
        const bench = bm.getCbetTextureBenchmark(c.key);
        if (evalBenchmark(c.cbetPct, bench) !== 'critical') return null;
        return {
          key: `cbet-tex-${c.key}`,
          label: `CBet ${texLabel[c.texture] ?? c.texture} ${c.posType.toUpperCase()}`,
          value: c.cbetPct,
        };
      })
      .filter(Boolean) as { key: string; label: string; value: number }[];

    // ── CBet Flop por textura — MULTIWAY ─────────────────────────────────────
    const textureMultiLeaks = stats.cbetFlopByTextureMulti
      .filter(c => c.opp >= MIN_SAMPLE_FOR_BENCHMARK_EVAL)
      .map(c => {
        const bench = bm.getCbetTextureMultiBenchmark(c.texture);
        if (evalBenchmark(c.cbetPct, bench) !== 'critical') return null;
        return {
          key: `cbet-tex-multi-${c.texture}`,
          label: `CBet ${texLabel[c.texture] ?? c.texture} (multi)`,
          value: c.cbetPct,
        };
      })
      .filter(Boolean) as { key: string; label: string; value: number }[];

    return [...scalarLeaks, ...textureLeaks, ...textureMultiLeaks];
  }, [stats, activeStack, bm]);

  const runAIAnalysis = async () => {
    if (!stats || !aggregated) return;
    setAiLoading(true);
    setAiError('');
    setAiAnalysis('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const jwt = sessionData?.session?.access_token;
      const base = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
      const response = await fetch(`${base}/api/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { 'Authorization': `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({
          stats,
          handsPlayed: aggregated.handsPlayed,
          stakeRange: null,
          segmentation,
          stackFilter: activeStack,
          lang,
        }),
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(t('stats.aiDevError'));
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'API error');
      setAiAnalysis(data.analysis || '');
    } catch (err: any) {
      const msg: string = err.message || '';
      if (msg.includes('ECONNREFUSED') || msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        setAiError(t('stats.aiDevError'));
      } else {
        setAiError(msg || t('stats.aiError'));
      }
    } finally {
      setAiLoading(false);
    }
  };

  // ─── JSX reutilizable: barra de filtro por fecha ────────────────────────
  const dateFilterBar = (
    <div className="date-filter">
      <div className="date-filter-presets">
        {(['all', 'last7', 'last30', 'thisMonth', 'lastMonth'] as const).map(preset => (
          <button
            key={preset}
            className={`stats-tab ${activePreset === preset ? 'active' : ''}`}
            onClick={() => {
              const range = computePresetRange(preset);
              setDateFrom(range.from);
              setDateTo(range.to);
              setActivePreset(preset);
            }}
          >
            {preset === 'all'       ? t('stats.dateFilter.allTime')
             : preset === 'last7'   ? t('stats.dateFilter.last7')
             : preset === 'last30'  ? t('stats.dateFilter.last30')
             : preset === 'thisMonth' ? t('stats.dateFilter.thisMonth')
             : t('stats.dateFilter.lastMonth')}
          </button>
        ))}
      </div>
      <div className="date-filter-custom">
        <label className="date-filter-label">{t('stats.dateFilter.from')}</label>
        <input
          type="date"
          className="date-filter-input"
          value={dateFrom}
          onChange={e => { setDateFrom(e.target.value); setActivePreset('custom'); }}
        />
        <label className="date-filter-label">{t('stats.dateFilter.to')}</label>
        <input
          type="date"
          className="date-filter-input"
          value={dateTo}
          onChange={e => { setDateTo(e.target.value); setActivePreset('custom'); }}
        />
      </div>
    </div>
  );

  if (!stats || !aggregated) {
    if (tournaments.length > 0) {
      // Hay torneos pero el filtro no retorna resultados
      return (
        <div className="stats-view">
          <div className="stats-header">
            <div>
              <h2 className="form-title">{t('stats.title')}</h2>
            </div>
          </div>
          {dateFilterBar}
          <div className="stats-empty">
            <p>{t('stats.dateFilter.noData')}</p>
          </div>
        </div>
      );
    }
    return (
      <div className="stats-empty">
        <p>{t('stats.empty')}</p>
      </div>
    );
  }

  // Helper: determinar si un matchup tiene sample suficiente para fold-to-3bet splits
  const foldTo3betAllinLow = stats.foldTo3betAllinOpp < 30;
  const foldTo3betNonAllinLow = stats.foldTo3betNonAllinOpp < 30;

  return (
    <div className="stats-view">
      <div className="stats-header">
        <div>
          <h2 className="form-title">{t('stats.title')}</h2>
          <p className="import-subtitle">
            {t('stats.subtitle', {
              hands: aggregated.handsPlayed.toLocaleString(),
              tournaments: filteredTournaments.length,
            })}
          </p>
        </div>
        {criticalLeaks.length > 0 && (
          <div className="leaks-summary">
            {criticalLeaks.slice(0, 4).map(l => (
              <span key={l.key} className="leak-badge">
                ⚠ {l.label}: {l.value}%
              </span>
            ))}
          </div>
        )}
      </div>

      {dateFilterBar}

      <div
        className="stack-filter"
        style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.5rem' }}
      >
        <button
          className={`stats-tab ${activeStack === 'all' ? 'active' : ''}`}
          onClick={() => setActiveStack('all')}
        >
          All
        </button>
        <button
          className={`stats-tab ${activeStack === 'short' ? 'active' : ''}`}
          onClick={() => setActiveStack('short')}
        >
          &lt;25BB
        </button>
        <button
          className={`stats-tab ${activeStack === 'mid' ? 'active' : ''}`}
          onClick={() => setActiveStack('mid')}
        >
          25-50BB
        </button>
        <button
          className={`stats-tab ${activeStack === 'deep' ? 'active' : ''}`}
          onClick={() => setActiveStack('deep')}
        >
          &gt;50BB
        </button>
      </div>

      <div className="stats-tabs">
        {(['preflop', 'flop', 'turn', 'river', 'showdown'] as const).map(s => (
          <button
            key={s}
            className={`stats-tab ${section === s ? 'active' : ''}`}
            onClick={() => setSection(s)}
          >
            {s === 'preflop'
              ? t('stats.tabPreflop')
              : s === 'flop'
              ? t('stats.tabFlop')
              : s === 'turn'
              ? t('stats.tabTurn')
              : s === 'river'
              ? t('stats.tabRiver')
              : t('stats.tabShowdown')}
          </button>
        ))}
      </div>

      <div className="stats-grid">
        {section === 'preflop' && (
          <>
            <StatRow label="VPIP" value={stats.vpip} activeStack={activeStack} statKey="vpip" />
            {/* ─── VPIP por posición (expandible) ─────────────────────────── */}
            <StatRow
              label={t('stats.vpip.rowLabel')}
              value={stats.vpip}
              activeStack={activeStack}
              statKey="__vpipByPos__"
              sampleSize={stats.vpipTotalOpp}
              expandable
              isExpanded={expandedVpipByPos}
              onToggleExpand={() => setExpandedVpipByPos(v => !v)}
            >
              <div className="stat-expand-content">
                {isPro ? (
                  <VpipBreakdown
                    vpip={stats.vpipByPosition}
                    label={t('stats.vpip.title')}
                  />
                ) : (
                  <UpgradePrompt feature={t('stats.vpip.title')} onUpgrade={onUpgrade} />
                )}
              </div>
            </StatRow>
            <StatRow label="PFR" value={stats.pfr} activeStack={activeStack} statKey="pfr" />
            <StatRow
              label="PFR/VPIP"
              value={stats.pfrVpipRatio}
              activeStack={activeStack}
              statKey="pfrVpipRatio"
            />
            {/* ─── FASE 4a: 3Bet con matchup breakdown expandible ────────── */}
            <StatRow
              label="3Bet"
              value={stats.threebet}
              activeStack={activeStack}
              statKey="threebet"
              sampleSize={aggregated.threebetOpp}
              expandable
              isExpanded={expanded3betMatchups}
              onToggleExpand={() => setExpanded3betMatchups(v => !v)}
            >
              <div className="stat-expand-content">
                {isPro ? (
                  <MatchupBreakdown
                    matchups={stats.threebetByMatchup}
                    label={t('stats.matchup.threebetTitle')}
                    valueColumnLabel={t('stats.matchup.thThreebet')}
                    showSuccessColumn={false}
                    getBenchmark={key => getThreebetMatchupBenchmark(key, activeStack)}
                  />
                ) : (
                  <UpgradePrompt feature={t('stats.matchup.threebetTitle')} onUpgrade={onUpgrade} />
                )}
              </div>
            </StatRow>

            {/* ─── FASE 2: Fold to 3Bet con split expandible ─────────────── */}
            {/* ─── FASE 4b: + matchup breakdown anidado ──────────────────── */}
            <StatRow
              label="Fold to 3Bet"
              value={stats.foldTo3bet}
              activeStack={activeStack}
              statKey="foldTo3bet"
              sampleSize={aggregated.foldTo3betOpp}
              expandable
              isExpanded={expandedFoldTo3bet}
              onToggleExpand={() => setExpandedFoldTo3bet(v => !v)}
            >
              <div className="stat-sub-rows">
                <StatRow
                  label={t('stats.foldTo3betVsAllin')}
                  value={stats.foldTo3betAllin}
                  activeStack={activeStack}
                  statKey="foldTo3betAllin"
                  sampleSize={stats.foldTo3betAllinOpp}
                  lowSample={foldTo3betAllinLow}
                  indent
                />
                <StatRow
                  label={t('stats.foldTo3betVsNonAllin')}
                  value={stats.foldTo3betNonAllin}
                  activeStack={activeStack}
                  statKey="foldTo3betNonAllin"
                  sampleSize={stats.foldTo3betNonAllinOpp}
                  lowSample={foldTo3betNonAllinLow}
                  indent
                />

                {/* Matchup breakdown anidado — solo Pro */}
                {isPro ? (
                  <>
                    <button
                      type="button"
                      className="matchup-toggle-link"
                      onClick={() => setExpandedFoldTo3betMatchups(v => !v)}
                    >
                      {expandedFoldTo3betMatchups
                        ? t('stats.foldTo3betHideMatchup')
                        : t('stats.foldTo3betShowMatchup')}
                    </button>
                    {expandedFoldTo3betMatchups && (
                      <div className="stat-expand-content">
                        <MatchupBreakdown
                          matchups={stats.foldTo3betByMatchup}
                          label={t('stats.matchup.foldTo3betTitle')}
                          valueColumnLabel={t('stats.matchup.thFold')}
                          showSuccessColumn={false}
                          getBenchmark={key => getFoldTo3betMatchupBenchmark(key, activeStack)}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <UpgradePrompt feature={t('stats.matchup.foldTo3betTitle')} onUpgrade={onUpgrade} />
                )}
              </div>
            </StatRow>

            {/* ─── FASE C: Fold to Push ───────────────────────────────────── */}
            <StatRow
              label={t('stats.foldToPush.rowLabel')}
              value={stats.foldToPush}
              activeStack={activeStack}
              statKey="__foldToPush__"
              sampleSize={stats.foldToPushOpp}
              expandable
              isExpanded={expandedFoldToPush}
              onToggleExpand={() => setExpandedFoldToPush(v => !v)}
            >
              <div className="stat-expand-content">
                {isPro ? (
                  <FoldToPushBreakdown
                    data={stats.foldToPushByShoverStack}
                    label={t('stats.foldToPush.title')}
                  />
                ) : (
                  <UpgradePrompt feature={t('stats.foldToPush.title')} onUpgrade={onUpgrade} />
                )}
              </div>
            </StatRow>

            {/* ─── FASE 3: Open Raise (RFI) por posición ─────────────────── */}
            <StatRow
              label={t('stats.openRaise')}
              value={stats.rfiTotal}
              activeStack={activeStack}
              statKey="__rfiAggregate__"
              sampleSize={stats.rfiTotalOpp}
              expandable
              isExpanded={expandedRfi}
              onToggleExpand={() => setExpandedRfi(v => !v)}
            >
              <div className="stat-expand-content">
                {isPro ? (
                  <RfiBreakdown
                    rfi={stats.rfiByPosition}
                    activeStack={activeStack}
                    label={t('stats.rfi.title')}
                  />
                ) : (
                  <UpgradePrompt feature={t('stats.rfi.title')} onUpgrade={onUpgrade} />
                )}
              </div>
            </StatRow>

            {/* ─── FASE A: Push/Fold short stack por posición ────────────── */}
            {/* Solo se muestra cuando el user filtró a 'short' stack — es donde
                push/fold theory aplica. En otros stacks la fila aparece dimmed
                con un mensaje pidiendo cambiar al filter de short stack. */}
            {activeStack === 'short' ? (
              <StatRow
                label={t('stats.pushFold.label')}
                value={stats.pushFoldTotalOpp > 0 ? Math.round(
                  (stats.pushFoldByPosition.reduce((s, p) => s + p.pushes, 0) /
                    stats.pushFoldTotalOpp) * 100
                ) : 0}
                activeStack={activeStack}
                statKey="__pushFoldAggregate__"
                sampleSize={stats.pushFoldTotalOpp}
                expandable
                isExpanded={expandedPushFold}
                onToggleExpand={() => setExpandedPushFold(v => !v)}
              >
                <div className="stat-expand-content">
                  {isPro ? (
                    <PushFoldBreakdown
                      pushFold={stats.pushFoldByPosition}
                      label={t('stats.pushFold.title')}
                    />
                  ) : (
                    <UpgradePrompt feature={t('stats.pushFold.title')} onUpgrade={onUpgrade} />
                  )}
                </div>
              </StatRow>
            ) : (
              <div className="pushfold-stack-hint">
                {t('stats.pushFold.switchStackHint')}
              </div>
            )}

            <StatRow label="ATS" value={stats.ats} activeStack={activeStack} statKey="ats" />

            <StatRow
              label={t('stats.squeeze')}
              value={stats.squeeze ?? 0}
              activeStack={activeStack}
              statKey="squeeze"
              sampleSize={stats.squeezeOpp}
            />
            <StatRow
              label={t('stats.fourbet')}
              value={stats.fourbet ?? 0}
              activeStack={activeStack}
              statKey="fourbet"
              sampleSize={stats.fourbetOpp}
            />
            <StatRow
              label={t('stats.openLimp')}
              value={stats.openLimp ?? 0}
              activeStack={activeStack}
              statKey="openLimp"
              sampleSize={stats.openLimpOpp}
            />
          </>
        )}

        {section === 'flop' && (
          <>
            {/* ─── CBET FLOP ────────────────────────────────────────────── */}
            <div className="stat-group-label">C-BET</div>
            <StatRow
              label="CBet Flop"
              value={stats.cbetFlop}
              activeStack={activeStack}
              statKey="cbetFlop"
              sampleSize={aggregated.cbetFlopOpp}
              expandable
              isExpanded={expandedCbetMatchups}
              onToggleExpand={() => setExpandedCbetMatchups(v => !v)}
            >
              <div className="stat-expand-content">
                {isPro ? (
                  <MatchupBreakdown
                    matchups={stats.cbetFlopByMatchup}
                    label={t('stats.matchup.cbetFlopTitle')}
                    getBenchmark={key => getCbetMatchupBenchmark(key, activeStack)}
                  />
                ) : (
                  <UpgradePrompt feature={t('stats.matchup.cbetFlopTitle')} onUpgrade={onUpgrade} />
                )}
              </div>
            </StatRow>
            <StatRow
              label={t('stats.cbetTexture.label')}
              value={
                stats.cbetFlopByTextureTotalOpp > 0
                  ? Math.round(
                      (stats.cbetFlopByTexture.reduce((s, c) => s + c.count, 0) /
                        stats.cbetFlopByTextureTotalOpp) * 100
                    )
                  : 0
              }
              activeStack={activeStack}
              statKey="__cbetFlopTexture__"
              sampleSize={stats.cbetFlopByTextureTotalOpp}
              expandable
              isExpanded={expandedCbetTexture}
              onToggleExpand={() => setExpandedCbetTexture(v => !v)}
            >
              <div className="stat-expand-content">
                {isPro ? (
                  <CbetTextureBreakdown
                    cbetTexture={stats.cbetFlopByTexture}
                    label={t('stats.cbetTexture.title')}
                  />
                ) : (
                  <UpgradePrompt feature={t('stats.cbetTexture.title')} onUpgrade={onUpgrade} />
                )}
              </div>
            </StatRow>
            <StatRow
              label={t('stats.cbetTextureMulti.label')}
              value={
                stats.cbetFlopByTextureMultiTotalOpp > 0
                  ? Math.round(
                      (stats.cbetFlopByTextureMulti.reduce((s, c) => s + c.count, 0) /
                        stats.cbetFlopByTextureMultiTotalOpp) * 100
                    )
                  : 0
              }
              activeStack={activeStack}
              statKey="__cbetFlopTextureMulti__"
              sampleSize={stats.cbetFlopByTextureMultiTotalOpp}
              expandable
              isExpanded={expandedCbetTextureMulti}
              onToggleExpand={() => setExpandedCbetTextureMulti(v => !v)}
            >
              <div className="stat-expand-content">
                {isPro ? (
                  <CbetTextureMultiBreakdown
                    cbetTextureMulti={stats.cbetFlopByTextureMulti}
                    label={t('stats.cbetTextureMulti.title')}
                  />
                ) : (
                  <UpgradePrompt feature={t('stats.cbetTextureMulti.title')} onUpgrade={onUpgrade} />
                )}
              </div>
            </StatRow>
            <StatRow
              label="CBet Flop Success"
              value={stats.cbetFlopSuccess}
              activeStack={activeStack}
              statKey="cbetFlopSuccess"
            />
            <StatRow
              label={t('stats.cbet3BetPot.rowLabel')}
              value={stats.cbet3BetPot}
              activeStack={activeStack}
              statKey="__cbet3BetPot__"
              sampleSize={stats.cbet3BetPotOpp}
              expandable
              isExpanded={expandedCbet3BetPot}
              onToggleExpand={() => setExpandedCbet3BetPot(v => !v)}
            >
              <div className="stat-expand-content">
                {isPro ? (
                  <Cbet3BetPotBreakdown
                    ip={stats.cbet3BetPotIP}
                    ipOpp={stats.cbet3BetPotIPOpp}
                    oop={stats.cbet3BetPotOOP}
                    oopOpp={stats.cbet3BetPotOOPOpp}
                    activeStack={activeStack}
                    label={t('stats.cbet3BetPot.title')}
                  />
                ) : (
                  <UpgradePrompt feature={t('stats.cbet3BetPot.title')} onUpgrade={onUpgrade} />
                )}
              </div>
            </StatRow>

            {/* ─── CHECK-RAISE ──────────────────────────────────────────── */}
            <div className="stat-group-label">{t('stats.checkRaise')}</div>
            <StatRow
              label="XRaise Flop"
              value={stats.xraiseFlopPct ?? 0}
              activeStack={activeStack}
              statKey="xraiseFlop"
              sampleSize={stats.xraiseFlopOpp}
            />
            <StatRow
              label="XRaise Flop (PFA)"
              value={stats.xraiseFlopAsPFAPct ?? 0}
              activeStack={activeStack}
              statKey="xraiseFlop"
              sampleSize={stats.xraiseFlopAsPFAOpp}
            />
            <StatRow
              label="XRaise Flop (non-PFA)"
              value={stats.xraiseFlopNonPFAPct ?? 0}
              activeStack={activeStack}
              statKey="xraiseFlop"
              sampleSize={stats.xraiseFlopNonPFAOpp}
            />

            {/* ─── VS CBET FLOP ─────────────────────────────────────────── */}
            <div className="stat-group-label">{t('stats.vsCbetFlop')}</div>
            <StatRow
              label="Fold"
              value={stats.foldToCbetFlop}
              activeStack={activeStack}
              statKey="foldToCbetFlop"
            />
            <StatRow
              label="Call"
              value={stats.callCbetFlop}
              activeStack={activeStack}
              statKey="callCbetFlop"
            />

            {/* ─── DONK ────────────────────────────────────────────────── */}
            <div className="stat-group-label">{t('stats.donkBets')}</div>
            <StatRow
              label="Donk Flop"
              value={stats.donkFlop}
              activeStack={activeStack}
              statKey="donkFlop"
            />
          </>
        )}

        {section === 'turn' && (
          <>
            {/* ─── CBET TURN ───────────────────────────────────────────── */}
            <div className="stat-group-label">C-BET</div>
            <StatRow
              label="Double Barrel"
              value={stats.cbetTurn}
              activeStack={activeStack}
              statKey="cbetTurn"
            />
            <StatRow
              label="Double Barrel Success"
              value={stats.cbetTurnSuccess}
              activeStack={activeStack}
              statKey="cbetTurnSuccess"
            />
            <StatRow
              label="Delayed CBet"
              value={stats.delayCbet}
              activeStack={activeStack}
              statKey="delayCbet"
              sampleSize={stats.delayCbetOpp}
            />

            {/* ─── VS CBET TURN ─────────────────────────────────────────── */}
            <div className="stat-group-label">{t('stats.vsCbetTurn')}</div>
            <StatRow
              label="Fold"
              value={stats.foldToCbetTurn}
              activeStack={activeStack}
              statKey="foldToCbetTurn"
            />
            <StatRow
              label="Call"
              value={stats.callCbetTurn}
              activeStack={activeStack}
              statKey="callCbetTurn"
            />

            {/* ─── DONK / PROBE ─────────────────────────────────────────── */}
            <div className="stat-group-label">{t('stats.donkBets')} / {t('stats.probeBets')}</div>
            <StatRow
              label="Donk Turn"
              value={stats.donkTurn}
              activeStack={activeStack}
              statKey="donkTurn"
            />
            <StatRow
              label="Probe Turn"
              value={stats.probeTurn}
              activeStack={activeStack}
              statKey="probeTotal"
            />
          </>
        )}

        {section === 'river' && (
          <>
            {/* ─── CBET RIVER ──────────────────────────────────────────── */}
            <div className="stat-group-label">C-BET</div>
            <StatRow
              label="Triple Barrel"
              value={stats.cbetRiver}
              activeStack={activeStack}
              statKey="cbetRiver"
            />
            <StatRow
              label="Triple Barrel Success"
              value={stats.cbetRiverSuccess}
              activeStack={activeStack}
              statKey="cbetRiverSuccess"
            />

            {/* ─── VS CBET RIVER ────────────────────────────────────────── */}
            <div className="stat-group-label">{t('stats.vsCbetRiver')}</div>
            <StatRow
              label="Fold"
              value={stats.foldToCbetRiver}
              activeStack={activeStack}
              statKey="foldToCbetRiver"
            />
            <StatRow
              label="Call"
              value={stats.callCbetRiver}
              activeStack={activeStack}
              statKey="callCbetRiver"
            />

            {/* ─── DONK / PROBE ─────────────────────────────────────────── */}
            <div className="stat-group-label">{t('stats.donkBets')} / {t('stats.probeBets')}</div>
            <StatRow
              label="Donk River"
              value={stats.donkRiver}
              activeStack={activeStack}
              statKey="donkRiver"
            />
            <StatRow
              label="Probe River"
              value={stats.probeRiver}
              activeStack={activeStack}
              statKey="probeTotal"
            />
            <StatRow
              label="Probe Total"
              value={stats.probeTotal}
              activeStack={activeStack}
              statKey="probeTotal"
            />
          </>
        )}

        {section === 'showdown' && (
          <>
            <StatRow label="WTSD" value={stats.wtsd} activeStack={activeStack} statKey="wtsd" />
            <StatRow label="W@SD" value={stats.wsd} activeStack={activeStack} statKey="wsd" />
            <StatRow label="WWSF" value={stats.wwsf} activeStack={activeStack} statKey="wwsf" />
            <StatRow label="AF" value={stats.af} statKey="af" unit="" activeStack={activeStack} />
            <StatRow label="AFq" value={stats.afq} statKey="afq" activeStack={activeStack} />
            <div className="stat-group-label" style={{ marginTop: '1.5rem' }}>
              {t('stats.positions')}
            </div>
            {Object.entries(stats.positionBreakdown)
              .filter(([, v]) => v > 0)
              .sort(([, a], [, b]) => b - a)
              .map(([pos, count]) => (
                <div key={pos} className="stat-row">
                  <span className="stat-label">{pos}</span>
                  <span className="stat-value">{count}</span>
                  <span className="stat-range">
                    {Math.round((100 * count) / aggregated.handsPlayed)}%
                  </span>
                  <span className="stat-dot dot-ok" />
                </div>
              ))}
          </>
        )}
      </div>

      {/* AI Panel */}
      <div className="ai-analysis-panel">
        <div className="ai-panel-header">
          <div>
            <h3 className="ai-panel-title">{t('stats.aiTitle')}</h3>
            <p className="ai-panel-sub">{t('stats.aiSub')}</p>
          </div>

          {plan === 'free' && (
            <div className="ai-paywall">
              <p className="ai-paywall-text">{t('stats.paywallText')}</p>
              <button className="btn btn-primary" onClick={onUpgrade}>
                {t('stats.paywallBtn')}
              </button>
            </div>
          )}

          {plan === 'pro' && !aiAnalysis && (
            <>
              <label className="consent-label">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                  className="import-check"
                />
                <span>{t('stats.aiConsent')}</span>
              </label>
              <button
                className="btn btn-primary"
                onClick={runAIAnalysis}
                disabled={aiLoading || !consent || aggregated.handsPlayed < 100}
              >
                {aiLoading ? (
                  <>
                    <span
                      className="dz-spinner"
                      style={{ width: 14, height: 14, display: 'inline-block', marginRight: 6 }}
                    />
                    {t('stats.aiLoading')}
                  </>
                ) : aggregated.handsPlayed < 100 ? (
                  t('stats.aiNeedHands', { n: 100, have: aggregated.handsPlayed })
                ) : (
                  t('stats.aiGenerate')
                )}
              </button>
            </>
          )}
        </div>

        {aiError && (
          <div className="auth-message err" style={{ marginTop: '1rem' }}>
            {aiError}
          </div>
        )}

        {aiLoading && (
          <div className="ai-loading">
            <div className="ai-loading-bar" />
            <span>{t('stats.aiAnalyzing', { n: aggregated.handsPlayed.toLocaleString() })}</span>
          </div>
        )}

        {aiAnalysis && (
          <div className="ai-result">
            {aiAnalysis
              .split('\n')
              .filter(l => l.trim())
              .map((line, i) => {
                if (line.startsWith('## '))
                  return (
                    <h4 key={i} className="ai-section-h">
                      {line.replace('## ', '')}
                    </h4>
                  );
                if (line.startsWith('**'))
                  return (
                    <p key={i} className="ai-leak-title">
                      {line.replace(/\*\*/g, '')}
                    </p>
                  );
                return (
                  <p key={i} className="ai-line">
                    {line}
                  </p>
                );
              })}
            <button
              className="btn btn-secondary"
              style={{ marginTop: '1rem' }}
              onClick={() => {
                setAiAnalysis('');
                setAiError('');
              }}
            >
              ↺ Regenerar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
